import React, { useState, useEffect, useCallback, useMemo, useRef, Suspense } from 'react';
import {
    Box,
    Drawer,
    AppBar,
    Toolbar,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    CircularProgress,
    ListItemText,
    Typography,
    IconButton,
    Menu,
    MenuItem,
    Avatar,
    Divider,
    TextField,
    InputAdornment,
    Paper,
    ClickAwayListener,
    Badge,
    Button,
    Tooltip,
    Collapse,
} from '@mui/material';

import {
    Logout as LogoutIcon,
    Menu as MenuIcon,
    AccountCircle as AccountCircleIcon,
    ArrowBack as ArrowBackIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
    Notifications as NotificationsIcon,
    Circle as CircleIcon,
    Security as SecurityIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Close as NotificationCloseIcon,
    Done as MarkReadIcon,
} from '@mui/icons-material';

import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { buildMenu } from './menu';
import { useAuth } from 'modules/auth/contexts/AuthContext';
import PunchButton from 'modules/attendance/components/PunchButton';
import OfflineBanner from 'core/components/OfflineBanner';
import api from 'core/services/api';
import { useVisibilityPolling } from 'shared/hooks/useVisibilityPolling';
import { connectNotificationSocket } from 'core/services/notificationSocket';
import { smartDateTime } from 'shared/utils/timeFormat';

const DRAWER_WIDTH   = 250;
const COLLAPSED_WIDTH = 64;
const TRANSITION     = 'width 0.25s cubic-bezier(0.4,0,0.2,1)';

// Notification types whose arrival means a sidebar badge count (see
// sectionBadges) may now be stale — triggers an immediate re-fetch instead
// of waiting for the next 90s poll.
const BADGE_REFRESH_TYPES = new Set([
    'ALLOWANCE_REQUEST', 'ALLOWANCE_APPROVED', 'ALLOWANCE_REJECTED',
    'PROFILE_UPDATE', 'PROFILE_UPDATE_APPROVED', 'PROFILE_UPDATE_REJECTED',
    'DEVICE_APPROVED', 'DEVICE_REJECTED',
    'CORRECTION_REQUEST', 'CORRECTION_APPROVED', 'CORRECTION_REJECTED',
    'FORGOT_PUNCH_SUBMITTED', 'FORGOT_PUNCH_APPROVED', 'FORGOT_PUNCH_REJECTED',
]);

// Notification types that only make sense inside the mobile app (a phone
// update prompt, a punch reminder/inactivity nudge, a field-employee
// customer assignment, a travel-companion tag) — meaningless to act on from
// a browser, so the web bell excludes them for every role rather than
// mixing them in with the approval/request notifications admins act on.
const APP_ONLY_NOTIFICATION_TYPES = [
    'APP_UPDATE_AVAILABLE', 'DAILY_PUNCH_REMINDER',
    'PUNCH_INACTIVITY', 'CUSTOMER_ASSIGNED', 'COMPANION_NAMED',
];
const APP_ONLY_TYPES_PARAM = APP_ONLY_NOTIFICATION_TYPES.join(',');

// ─── AppLayout ───────────────────────────────────────────────────────────────

const AppLayout = ({ children }) => {

    const navigate  = useNavigate();
    const location  = useLocation();
    const { user, logout, userRole, effectiveRole, isCustomRole } = useAuth();

    const [mobileOpen,        setMobileOpen]        = useState(false);
    const [sidebarCollapsed,  setSidebarCollapsed]  = useState(
        () => localStorage.getItem('sidebarCollapsed') === 'true'
    );
    // Explicit user toggles for collapsible sidebar groups (Employee
    // Management, Employee, Role & Access) — keyed by group text. Absent
    // from this map means "use the default" (auto-open if it contains the
    // active route), see groupContainsPath/isGroupExpanded below.
    const [expandedGroups,    setExpandedGroups]    = useState({});
    // Open/closed groups are remembered per user on this browser.
    const groupsStorageKey = `sidebarGroups:${user?.id ?? 'anon'}`;
    useEffect(() => {
        try { setExpandedGroups(JSON.parse(localStorage.getItem(groupsStorageKey) || '{}')); } catch { setExpandedGroups({}); }
    }, [groupsStorageKey]);
    const [anchorEl,          setAnchorEl]          = useState(null);
    const [searchQuery,       setSearchQuery]       = useState('');
    const [searchOpen,        setSearchOpen]        = useState(false);
    const [notificationAnchor,setNotificationAnchor]= useState(null);
    const [notifications,     setNotifications]     = useState([]);
    const [unreadCount,       setUnreadCount]       = useState(0);
    const [enabledFeatures,   setEnabledFeatures]   = useState({});
    const [sectionBadges,     setSectionBadges]     = useState({});

    const drawerWidth = sidebarCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;

    const toggleSidebar = () => {
        setSidebarCollapsed(prev => {
            localStorage.setItem('sidebarCollapsed', String(!prev));
            return !prev;
        });
        // close search when collapsing
        if (!sidebarCollapsed) { setSearchQuery(''); setSearchOpen(false); }
    };

    // ── Features ──────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!user) return;
        if (effectiveRole === 'EMPLOYEE' && !isCustomRole) return;

        // Always fetched fresh — this drives access control, not just display,
        // so a Super Admin changing a role's/user's features must be reflected
        // the moment the affected person reloads. A time-based cache here
        // (previously sessionStorage, up to 5 minutes, surviving reloads since
        // sessionStorage isn't cleared by navigation) meant access changes
        // — including revocations — could silently not take effect for
        // minutes after being saved.
        let cancelled = false;
        const fetchFeatures = async () => {
            try {
                const res          = await api.getUserFeaturesByUser(user.id);
                if (cancelled) return;
                const featuresData = res.data || [];
                const featureMap   = {};
                featuresData.forEach(f => { featureMap[f.code] = f.is_enabled; });
                setEnabledFeatures(featureMap);
            } catch (err) {
                // A failure here used to be a silent dead end: enabledFeatures
                // stayed at whatever it was (empty on first load, stale after
                // a role change) with nothing to ever refresh it again. Since
                // this drives access control, that's not cosmetic - it can
                // mean a custom role's menu stays wrong (missing, or stale
                // and not yet reflecting a revocation) indefinitely. Retry
                // once the server is confirmed reachable again, same signal
                // OfflineBanner already listens for (api.js's reconnect
                // polling) - no new infrastructure.
                console.error('Failed to fetch features:', err);
            }
        };
        fetchFeatures();

        const onServerOnline = () => { if (!cancelled) fetchFeatures(); };
        window.addEventListener('tas:server:online', onServerOnline);
        return () => {
            cancelled = true;
            window.removeEventListener('tas:server:online', onServerOnline);
        };
    }, [user, userRole, effectiveRole, isCustomRole]);

    // ── Notifications ─────────────────────────────────────────────────────────
    const fetchNotifications = useCallback(async () => {
        if (!user) return;
        try {
            const [countRes, notifRes] = await Promise.all([
                api.getUnreadNotificationCount({ exclude_types: APP_ONLY_TYPES_PARAM }),
                api.getNotifications({ page_size: 10, exclude_types: APP_ONLY_TYPES_PARAM }),
            ]);
            setUnreadCount(countRes.data.unread_count);
            setNotifications(notifRes.data.results || notifRes.data);
        } catch {
            // non-critical — silently ignore
        }
    }, [user]);

    // Fire once on mount, then poll every 90s — paused while tab is hidden.
    // Polling stays on as the reconciliation fallback even with the socket
    // below connected (same pattern as EmployeeTracking + trackingSocket).
    useEffect(() => { fetchNotifications(); }, [fetchNotifications]);
    useVisibilityPolling(fetchNotifications, 90_000, !!user);

    // Per-section sidebar badges — each key mirrors a menu item's `badgeKey`
    // and holds the real pending count from that section's own model (not a
    // proxy via the Notification table, which has no "submitted" event for
    // every section — e.g. device requests only notify on the outcome, not
    // on submission), so a badge is only ever wrong if the underlying page's
    // own list would also be wrong.
    const fetchSectionBadges = useCallback(async () => {
        if (!user || !['ADMIN', 'SUPER_ADMIN'].includes(effectiveRole)) return;
        try {
            // Punch Review Center bundles 4 independent review queues into one
            // sidebar entry, so its badge is their sum — mirrors how
            // PunchReviewCenter.jsx itself combines locationPending +
            // correctionPending + forgotPunchPending across its 3 tabs (plus
            // the separate legacy /attendance/corrections/ queue).
            const jobs = [
                { key: 'PENDING_APPROVALS', request: api.getPendingApprovalsCount() },
                { key: 'PUNCH_REVIEW', request: api.getLocationExceptionsPendingCount() },
                { key: 'PUNCH_REVIEW', request: api.getCorrectionsPendingCount() },
                { key: 'PUNCH_REVIEW', request: api.getCorrectionRequestsPendingCount() },
                { key: 'PUNCH_REVIEW', request: api.getForgotPunchPendingCount() },
            ];
            if (enabledFeatures.PROFILE_APPROVALS !== false) {
                jobs.push({ key: 'PROFILE_APPROVALS', request: api.getProfilePendingCount() });
            }
            if (enabledFeatures.PENDING_APPROVALS !== false) {
                jobs.push({ key: 'COLLECTION_CORRECTIONS', request: api.getCollectionCorrectionsPendingCount() });
            }
            if (effectiveRole === 'SUPER_ADMIN' && enabledFeatures.DEVICE_MANAGEMENT !== false) {
                jobs.push({ key: 'DEVICE_MANAGEMENT', request: api.getDeviceDashboardSummary() });
            }

            const results = await Promise.allSettled(jobs.map(j => j.request));
            const next = {};
            results.forEach((result, i) => {
                if (result.status !== 'fulfilled') return;
                const { key } = jobs[i];
                const value = key === 'DEVICE_MANAGEMENT'
                    ? result.value.data.pending_devices
                    : result.value.data.pending_count;
                next[key] = (next[key] || 0) + (value || 0);
            });
            setSectionBadges(next);
        } catch {
            // non-critical — silently ignore
        }
    }, [user, effectiveRole, enabledFeatures]);

    useEffect(() => { fetchSectionBadges(); }, [fetchSectionBadges]);
    useVisibilityPolling(fetchSectionBadges, 90_000, !!user);

    // Real-time push over the existing apps.notifications Channels/Redis
    // layer (apps.organization.services.notify_user already broadcasts here
    // server-side) — makes a new notification appear immediately instead of
    // waiting up to 90s, without replacing the polling fallback above.
    //
    // fetchSectionBadges is read via a ref, not a dependency — its identity
    // changes whenever enabledFeatures/effectiveRole change (which can
    // happen shortly after mount, while feature flags are still loading),
    // and depending on it directly re-ran this effect mid-handshake,
    // closing the socket before onopen ever fired (the "WebSocket is closed
    // before the connection is established" console warning). The ref keeps
    // onMessage calling the current fetchSectionBadges without reconnecting
    // the socket every time it changes identity.
    const fetchSectionBadgesRef = useRef(fetchSectionBadges);
    fetchSectionBadgesRef.current = fetchSectionBadges;
    useEffect(() => {
        if (!user) return undefined;
        const disconnect = connectNotificationSocket({
            onMessage: (data) => {
                if (!data || data.id == null) return;
                if (APP_ONLY_NOTIFICATION_TYPES.includes(data.notification_type)) return;
                setNotifications(prev => {
                    if (prev.some(n => n.id === data.id)) return prev; // de-dupe by ID
                    const incoming = {
                        id: data.id,
                        notification_type: data.notification_type,
                        title: data.title,
                        message: data.message,
                        related_id: data.related_id,
                        related_type: data.related_type,
                        priority: data.priority || 'NORMAL',
                        action_url: data.action_url || '',
                        is_read: false,
                        is_dismissed: false,
                        created_at: data.created_at,
                    };
                    return [incoming, ...prev].slice(0, 10);
                });
                setUnreadCount(prev => prev + 1);
                if (BADGE_REFRESH_TYPES.has(data.notification_type)) fetchSectionBadgesRef.current();
            },
        });
        return disconnect;
    }, [user]);

    // ── Menu (must be before early return — Rules of Hooks) ───────────────────
    const menuItems = useMemo(
        () => (user ? buildMenu({ effectiveRole, isCustomRole, enabledFeatures }) : []),
        [user, enabledFeatures, effectiveRole, isCustomRole],
    );

    const navigateTo = useCallback((path) => {
        setMobileOpen(false);
        setSearchQuery('');
        setSearchOpen(false);
        if (location.pathname === path) return;
        navigate(path);
    }, [location.pathname, navigate]);

    if (!user) return null;

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleDrawerToggle = () => setMobileOpen(prev => !prev);

    const handleBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate(['ADMIN', 'SUPER_ADMIN'].includes(effectiveRole) ? '/admin/dashboard' : '/employee/dashboard');
        }
    };

    const showBackButton = !['/employee/dashboard', '/admin/dashboard'].includes(location.pathname);

    // Flattens group children into the same navigable-leaf shape the search
    // dropdown expects (it navigates by item.path, which a group itself
    // doesn't have) — so searching "password" still finds Password
    // Management even though it now lives inside Employee Management.
    const flattenLeaves = (menu) => menu.flatMap(item =>
        item.type === 'group' ? flattenLeaves(item.children) : [item]
    );

    const getSearchResults = () => {
        if (!searchQuery.trim()) return [];
        const q = searchQuery.toLowerCase();
        return flattenLeaves(menuItems).filter(item => item.text.toLowerCase().includes(q));
    };

    // Drives the topbar heading on desktop, where the permanent sidebar
    // already carries full branding — repeating it in the AppBar too would
    // just be dead space that could instead orient the user to the page
    // they're on.
    // A sub-page (e.g. /admin/hierarchy/tree/42) belongs to its menu leaf,
    // so neither the title nor the open group falls back to "Dashboard".
    const leafMatches = (path, pathname) => pathname === path || pathname.startsWith(`${path}/`);
    const currentPageTitle = flattenLeaves(menuItems)
        .filter(item => leafMatches(item.path, location.pathname))
        .sort((a, b) => b.path.length - a.path.length)[0]?.text || 'Dashboard';

    const groupContainsPath = (item, pathname) =>
        item.type === 'group'
            ? item.children.some(child => groupContainsPath(child, pathname))
            : leafMatches(item.path, pathname);

    // No explicit toggle yet -> default to open if the active page is inside
    // this group (so landing on e.g. Password Management via a direct link
    // or refresh doesn't hide it inside a collapsed group).
    const isGroupExpanded = (item) =>
        expandedGroups[item.text] ?? groupContainsPath(item, location.pathname);

    const toggleGroup = (item) =>
        setExpandedGroups(prev => {
            const next = { ...prev, [item.text]: !isGroupExpanded(item) };
            try { localStorage.setItem(groupsStorageKey, JSON.stringify(next)); } catch { /* private mode */ }
            return next;
        });

    // Renders one menu node — a plain navigable leaf, or an expandable group
    // (Employee Management, Employee, Role & Access) whose children render
    // through this same function, so a group can itself contain another
    // group. Collapsed (icon-only) sidebar mode skips group headers entirely
    // and lists their children as plain icons instead — building a hover-flyout
    // submenu for that state isn't worth it for a handful of admin-only items.
    const renderMenuNode = (item, isMobile, depth = 0) => {
        const collapsed = sidebarCollapsed && !isMobile;

        if (item.type === 'group') {
            if (collapsed) {
                return item.children.map(child => renderMenuNode(child, isMobile, depth));
            }
            const expanded = isGroupExpanded(item);
            const active = groupContainsPath(item, location.pathname);
            return (
                <React.Fragment key={item.text}>
                    <ListItem disablePadding>
                        <ListItemButton
                            onClick={() => toggleGroup(item)}
                            sx={{
                                borderRadius: 2,
                                pl: 2 + depth * 1.5,
                                pr: 1,
                                mb: 0.5,
                                minHeight: 42,
                                '& .MuiListItemIcon-root': { minWidth: 32, color: active ? '#d32f2f' : '#666' },
                                '&:hover': { backgroundColor: '#f5f5f5' },
                            }}
                        >
                            <ListItemIcon>
                                <item.icon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                primary={item.text}
                                primaryTypographyProps={{
                                    fontSize: 14, fontWeight: 600,
                                    color: active ? '#d32f2f' : '#333',
                                }}
                            />
                            {expanded ? <ExpandLessIcon fontSize="small" sx={{ color: '#999' }} /> : <ExpandMoreIcon fontSize="small" sx={{ color: '#999' }} />}
                        </ListItemButton>
                    </ListItem>
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                        {item.children.map(child => renderMenuNode(child, isMobile, depth + 1))}
                    </Collapse>
                </React.Fragment>
            );
        }

        const isActive = location.pathname === item.path;
        const badgeCount = item.badgeKey ? sectionBadges[item.badgeKey] : undefined;
        const hasBadge = Boolean(badgeCount);
        const btn = (
            <ListItemButton
                onClick={() => navigateTo(item.path)}
                sx={{
                    borderRadius: 2,
                    px: collapsed ? 1 : 2,
                    pl: collapsed ? 1 : 2 + depth * 1.5,
                    mb: 0.5,
                    minHeight: 42,
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    backgroundColor: isActive ? '#d32f2f' : 'transparent',
                    color: isActive ? '#fff' : '#333',
                    '& .MuiListItemIcon-root': {
                        minWidth: collapsed ? 0 : 28,
                        color: isActive ? '#fff' : '#666',
                        justifyContent: 'center',
                    },
                    '&:hover': {
                        backgroundColor: isActive ? '#b71c1c' : '#f5f5f5',
                    },
                }}
            >
                <ListItemIcon>
                    {hasBadge && collapsed ? (
                        <Badge badgeContent={badgeCount} color="error" max={99} overlap="circular">
                            {depth > 0 ? <CircleIcon sx={{ fontSize: 8 }} /> : <item.icon fontSize="small" />}
                        </Badge>
                    ) : (
                        depth > 0 ? <CircleIcon sx={{ fontSize: 8 }} /> : <item.icon fontSize="small" />
                    )}
                </ListItemIcon>
                {!collapsed && (
                    <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{ fontSize: 14, fontWeight: isActive ? 600 : 500 }}
                    />
                )}
                {!collapsed && hasBadge && (
                    <Badge
                        badgeContent={badgeCount}
                        color="error"
                        max={99}
                        sx={{ mr: 1.5, '& .MuiBadge-badge': { position: 'static', transform: 'none' } }}
                    />
                )}
            </ListItemButton>
        );

        return (
            <ListItem key={item.text} disablePadding>
                {collapsed ? (
                    <Tooltip title={item.text} placement="right" arrow>
                        {btn}
                    </Tooltip>
                ) : btn}
            </ListItem>
        );
    };

    const handleMarkAllRead = async () => {
        const prevNotifications = notifications;
        const prevUnreadCount = unreadCount;
        // Optimistic UI — reverted below if the backend call fails, so the
        // user never ends up with a UI state that lies about what's saved.
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        setUnreadCount(0);
        try {
            await api.markAllNotificationsRead();
        } catch (err) {
            console.error('Failed to mark all as read:', err);
            setNotifications(prevNotifications);
            setUnreadCount(prevUnreadCount);
        }
    };

    const handleDismissAll = async () => {
        const prevNotifications = notifications;
        const prevUnreadCount = unreadCount;
        setNotifications([]);
        setUnreadCount(0);
        try {
            await api.dismissAllNotifications();
        } catch (err) {
            console.error('Failed to clear all notifications:', err);
            setNotifications(prevNotifications);
            setUnreadCount(prevUnreadCount);
        }
    };

    // Marks one notification read in place, without navigating anywhere —
    // distinct from handleNotificationClick, which also opens the item.
    const handleMarkRead = async (e, notif) => {
        e.stopPropagation();
        if (notif.is_read) return;
        setNotifications(prev => prev.map(n => (n.id === notif.id ? { ...n, is_read: true } : n)));
        setUnreadCount(prev => Math.max(0, prev - 1));
        try {
            await api.markNotificationRead(notif.id);
        } catch (err) {
            console.error('Failed to mark notification as read:', err);
            setNotifications(prev => prev.map(n => (n.id === notif.id ? { ...n, is_read: false } : n)));
            setUnreadCount(prev => prev + 1);
        }
    };

    // Removes one notification from the active list (soft — backend keeps
    // the row for audit via is_dismissed, see NotificationViewSet.dismiss).
    const handleDismiss = async (e, notif) => {
        e.stopPropagation();
        setNotifications(prev => prev.filter(n => n.id !== notif.id));
        if (!notif.is_read) setUnreadCount(prev => Math.max(0, prev - 1));
        try {
            await api.dismissNotification(notif.id);
        } catch (err) {
            console.error('Failed to clear notification:', err);
            setNotifications(prev => (prev.some(n => n.id === notif.id) ? prev : [notif, ...prev]));
            if (!notif.is_read) setUnreadCount(prev => prev + 1);
        }
    };

    // Notification types that open a specific screen when clicked. Falls
    // back to notification.action_url (set server-side for types that
    // already know their exact destination) when there's no local mapping.
    // Types left out on purpose (ACCOUNT_BLOCKED/UNBLOCKED, DEVICE_APPROVED/
    // REJECTED, COMPANION_NAMED, APP_UPDATE_AVAILABLE, GENERAL,
    // MASTER_DATA_APPROVED/REJECTED) are informational only — there's no
    // dedicated screen for them to open.
    const NOTIFICATION_ROUTES = {
        DUPLICATE_COLLECTIONS: '/admin/duplicate-collections',
        // Admin/Super Admin side
        // APPROVAL_PENDING/DELEGATED/REMINDER/ESCALATED aren't mapped here —
        // they're raised by the module-agnostic hierarchy engine, so the
        // right screen depends on which module the underlying request
        // belongs to. The engine sets notif.action_url per-instance instead
        // (see apps.hierarchy.engine.resolve_approval_action_url).
        ALLOWANCE_REQUEST: '/admin/pending-approvals',
        PROFILE_UPDATE: '/admin/profile-approval',
        CORRECTION_REQUEST: '/admin/punch-review',
        FORGOT_PUNCH_SUBMITTED: '/admin/punch-review',
        MASTER_DATA_REQUEST: '/admin/master-data',
        // Employee side
        ALLOWANCE_APPROVED: '/employee/allowance-history',
        ALLOWANCE_REJECTED: '/employee/allowance-history',
        CORRECTION_APPROVED: '/employee/punch-corrections',
        CORRECTION_REJECTED: '/employee/punch-corrections',
        PUNCH_INACTIVITY: '/employee/punch-history',
        CUSTOMER_ASSIGNED: '/employee/customer-map',
        // Either side
        PROFILE_UPDATE_APPROVED: '/profile',
        PROFILE_UPDATE_REJECTED: '/profile',
    };

    const handleNotificationClick = async (notif) => {
        setNotificationAnchor(null);
        if (!notif.is_read) {
            setNotifications(prev => prev.map(n => (n.id === notif.id ? { ...n, is_read: true } : n)));
            setUnreadCount(prev => Math.max(0, prev - 1));
            api.markNotificationRead(notif.id).catch(() => {});
        }
        const route = NOTIFICATION_ROUTES[notif.notification_type] || notif.action_url;
        if (route) navigateTo(route);
    };

    // ── Sidebar content ───────────────────────────────────────────────────────
    const drawerContent = (isMobile = false) => (
        <Box sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRight: '1px solid #eee',
            overflow: 'hidden',
        }}>

            {/* HEADER */}
            <Box sx={{
                px: sidebarCollapsed && !isMobile ? 0 : 2,
                py: 1.5,
                borderBottom: '1px solid #eee',
                display: 'flex',
                alignItems: 'center',
                justifyContent: sidebarCollapsed && !isMobile ? 'center' : 'space-between',
                minHeight: 70,
                flexShrink: 0,
                background: sidebarCollapsed && !isMobile ? 'transparent' : 'linear-gradient(135deg,#fff8f8 0%,#fff 100%)',
            }}>
                {/* Expanded: logo + brand text */}
                {(!sidebarCollapsed || isMobile) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, overflow: 'hidden' }}>
                        <Box sx={{
                            width: 42, height: 42, borderRadius: '11px', overflow: 'hidden', flexShrink: 0,
                            border: '1.5px solid #fce4e4',
                            boxShadow: '0 2px 10px rgba(211,47,47,0.15)',
                            bgcolor: '#fff',
                        }}>
                            <img src="/namra-logo.png" alt="Namra"
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        </Box>
                        <Box sx={{ overflow: 'hidden' }}>
                            <Typography sx={{
                                fontWeight: 800, color: '#d32f2f', fontSize: 15,
                                letterSpacing: '-0.3px', lineHeight: 1.1, whiteSpace: 'nowrap',
                            }}>
                                NamraCred
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: '#444', fontWeight: 600, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                                Namra Finance Ltd.
                            </Typography>
                            <Typography sx={{ fontSize: 8.5, color: '#999', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                                A subsidiary of Arman Financial Services Ltd.
                            </Typography>
                        </Box>
                    </Box>
                )}
                {/* Collapsed: just the logo */}
                {sidebarCollapsed && !isMobile && (
                    <Box sx={{
                        width: 36, height: 36, borderRadius: '9px', overflow: 'hidden',
                        border: '1.5px solid #fce4e4',
                        boxShadow: '0 2px 8px rgba(211,47,47,0.12)',
                        bgcolor: '#fff',
                    }}>
                        <img src="/namra-logo.png" alt="N"
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    </Box>
                )}
                {/* Collapse toggle — desktop only */}
                {!isMobile && (
                    <Tooltip title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
                        <IconButton
                            onClick={toggleSidebar}
                            size="small"
                            sx={{
                                bgcolor: '#f5f5f5',
                                border: '1px solid #e0e0e0',
                                width: 26, height: 26,
                                flexShrink: 0,
                                '&:hover': { bgcolor: '#ffebee', borderColor: '#d32f2f' },
                            }}
                        >
                            {sidebarCollapsed
                                ? <ChevronRightIcon sx={{ fontSize: 16, color: '#d32f2f' }} />
                                : <ChevronLeftIcon  sx={{ fontSize: 16, color: '#d32f2f' }} />
                            }
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            {/* SEARCH BAR — only when expanded */}
            {(!sidebarCollapsed || isMobile) && ['ADMIN', 'SUPER_ADMIN'].includes(effectiveRole) && (
                <Box sx={{ px: 2, py: 2, borderBottom: '1px solid #eee', position: 'relative', flexShrink: 0 }}>
                    <TextField
                        fullWidth size="small" placeholder="Search menu..."
                        value={searchQuery}
                        onChange={e => { setSearchQuery(e.target.value); setSearchOpen(true); }}
                        onFocus={() => setSearchOpen(true)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: 'action.active', fontSize: 20 }} />
                                </InputAdornment>
                            ),
                            endAdornment: searchQuery && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => { setSearchQuery(''); setSearchOpen(false); }}>
                                        <ClearIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    {searchOpen && searchQuery && (
                        <Paper sx={{ position: 'absolute', left: 0, right: 0, mx: 2, mt: 0.5,
                                     maxHeight: 300, overflow: 'auto', zIndex: 1000 }} elevation={4}>
                            <ClickAwayListener onClickAway={() => setSearchOpen(false)}>
                                <List dense>
                                    {getSearchResults().length > 0 ? (
                                        getSearchResults().map(item => (
                                            <ListItem key={item.text} disablePadding>
                                                <ListItemButton onClick={() => navigateTo(item.path)}
                                                    sx={{ py: 1, '&:hover': { backgroundColor: '#f5f5f5' } }}>
                                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                                        <item.icon fontSize="small" />
                                                    </ListItemIcon>
                                                    <ListItemText primary={item.text}
                                                        primaryTypographyProps={{ fontSize: 14 }} />
                                                </ListItemButton>
                                            </ListItem>
                                        ))
                                    ) : (
                                        <ListItem>
                                            <ListItemText primary="No results found"
                                                primaryTypographyProps={{ fontSize: 13, color: 'text.secondary',
                                                    sx: { textAlign: 'center', py: 2 } }} />
                                        </ListItem>
                                    )}
                                </List>
                            </ClickAwayListener>
                        </Paper>
                    )}
                </Box>
            )}

            {/* MENU LIST */}
            <List sx={{
                px: sidebarCollapsed && !isMobile ? 0.5 : 1,
                py: 1.5,
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
            }}>
                {menuItems.map((item, index) => {
                    const collapsed = sidebarCollapsed && !isMobile;
                    const showSectionHeader = item.section && item.section !== menuItems[index - 1]?.section;

                    return (
                        <React.Fragment key={item.text}>
                            {showSectionHeader && (
                                <>
                                    <Divider sx={{ my: 1 }} />
                                    {!collapsed && (
                                        <Typography
                                            variant="overline"
                                            sx={{ px: 2, color: '#999', fontWeight: 700, fontSize: 11, letterSpacing: 0.8 }}
                                        >
                                            {item.section}
                                        </Typography>
                                    )}
                                </>
                            )}
                            {renderMenuNode(item, isMobile)}
                        </React.Fragment>
                    );
                })}
            </List>

            {/* PUNCH BUTTON */}
            {effectiveRole === 'EMPLOYEE' && (
                <Box sx={{ p: sidebarCollapsed && !isMobile ? 1 : 2, flexShrink: 0 }}>
                    {sidebarCollapsed && !isMobile ? (
                        <Tooltip title="Punch In / Out" placement="right" arrow>
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <PunchButton iconOnly />
                            </Box>
                        </Tooltip>
                    ) : (
                        <PunchButton fullWidth variant="contained" />
                    )}
                </Box>
            )}

        </Box>
    );

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <Box sx={{ display: 'flex', backgroundColor: '#f5f5f5' }}>

            {/* TOPBAR */}
            <AppBar
                position="fixed"
                sx={{
                    backgroundColor: '#d32f2f',
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    ml: { sm: `${drawerWidth}px` },
                    transition: TRANSITION,
                }}
            >
                <Toolbar>
                    <IconButton color="inherit" edge="start" onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { sm: 'none' } }}>
                        <MenuIcon />
                    </IconButton>

                    {showBackButton && (
                        <IconButton color="inherit" onClick={handleBack}>
                            <ArrowBackIcon />
                        </IconButton>
                    )}

                    {/* Brand identity — shown only on mobile, where the permanent sidebar
                        (which already carries the full brand block) is hidden. On desktop
                        this space shows the current page title instead of repeating the
                        sidebar's branding verbatim. */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, overflow: 'hidden' }}>
                        <Box sx={{
                            width: 30, height: 30, borderRadius: '8px', overflow: 'hidden', flexShrink: 0,
                            bgcolor: '#fff', border: '1px solid rgba(255,255,255,0.3)',
                            boxShadow: '0 1px 6px rgba(0,0,0,0.2)',
                            display: { xs: 'flex', sm: 'none' },
                        }}>
                            <img src="/namra-logo.png" alt="Namra"
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        </Box>
                        <Box sx={{ overflow: 'hidden', display: { xs: 'block', sm: 'none' } }}>
                            <Typography sx={{
                                fontWeight: 800, color: '#fff', fontSize: 14,
                                letterSpacing: '-0.2px', lineHeight: 1, whiteSpace: 'nowrap',
                            }}>
                                NamraCred
                            </Typography>
                            <Typography sx={{
                                fontSize: 10, color: 'rgba(255,255,255,0.78)', lineHeight: 1.2,
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            }}>
                                Namra Finance Ltd. · A wholly owned subsidiary of Arman Financial Services Ltd.
                            </Typography>
                        </Box>
                        <Typography sx={{
                            display: { xs: 'none', sm: 'block' },
                            fontWeight: 700, color: '#fff', fontSize: 17, letterSpacing: '-0.2px',
                        }}>
                            {currentPageTitle}
                        </Typography>
                    </Box>

                    {/* USER SECTION */}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton color="inherit" onClick={e => setNotificationAnchor(e.currentTarget)}>
                            <Badge badgeContent={unreadCount} color="warning" max={99}>
                                <NotificationsIcon />
                            </Badge>
                        </IconButton>

                        {/* NOTIFICATION DROPDOWN */}
                        <Menu
                            anchorEl={notificationAnchor}
                            open={Boolean(notificationAnchor)}
                            onClose={() => setNotificationAnchor(null)}
                            PaperProps={{ sx: { width: 380, maxHeight: 440 } }}
                        >
                            <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                    <Typography variant="subtitle1" fontWeight="bold">Notifications</Typography>
                                    {unreadCount > 0 && (
                                        <Typography variant="caption" color="text.secondary">{unreadCount} unread</Typography>
                                    )}
                                </Box>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    {unreadCount > 0 && (
                                        <Button size="small" onClick={handleMarkAllRead}>Mark all read</Button>
                                    )}
                                    {notifications.length > 0 && (
                                        <Button size="small" color="inherit" onClick={handleDismissAll}>Clear all</Button>
                                    )}
                                </Box>
                            </Box>
                            <Divider />
                            <Box sx={{ maxHeight: 340, overflowY: 'auto' }}>
                                {notifications.length > 0 ? notifications.map(notif => {
                                    const isUrgent = notif.priority === 'CRITICAL' || notif.priority === 'HIGH';
                                    return (
                                    <MenuItem key={notif.id} onClick={() => handleNotificationClick(notif)}
                                        sx={{
                                            backgroundColor: notif.is_read ? 'transparent' : 'rgba(211,47,47,0.08)',
                                            whiteSpace: 'normal', py: 1.5,
                                            borderLeft: isUrgent ? '3px solid #d32f2f' : '3px solid transparent',
                                            '&:hover .notif-actions': { opacity: 1 },
                                        }}>
                                        <Box sx={{ width: '100%' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                        {!notif.is_read && <CircleIcon sx={{ fontSize: 8, color: '#d32f2f', mr: 1, flexShrink: 0 }} />}
                                                        <Typography variant="subtitle2" sx={{ fontWeight: notif.is_read ? 'normal' : 'bold' }}>
                                                            {notif.title}
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="body2" color="text.secondary" sx={{ ml: notif.is_read ? 0 : 2 }}>
                                                        {notif.message}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ ml: notif.is_read ? 0 : 2 }}>
                                                        {smartDateTime(notif.created_at)}
                                                    </Typography>
                                                </Box>
                                                <Box className="notif-actions" sx={{ display: 'flex', flexShrink: 0, opacity: 0, transition: 'opacity 0.15s' }}>
                                                    {!notif.is_read && (
                                                        <Tooltip title="Mark as read">
                                                            <IconButton size="small" onClick={(e) => handleMarkRead(e, notif)}>
                                                                <MarkReadIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    <Tooltip title="Clear">
                                                        <IconButton size="small" onClick={(e) => handleDismiss(e, notif)}>
                                                            <NotificationCloseIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </MenuItem>
                                    );
                                }) : (
                                    <Box sx={{ p: 3, textAlign: 'center' }}>
                                        <Typography color="text.secondary">No notifications</Typography>
                                    </Box>
                                )}
                            </Box>
                        </Menu>

                        <Box sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}>
                            <Typography>{[user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.username || user?.employee_id}</Typography>
                            <Typography sx={{ fontSize: 12 }}>{[userRole, user?.employee_id].filter(Boolean).join(' · ')}</Typography>
                        </Box>

                        <IconButton onClick={e => setAnchorEl(e.currentTarget)} color="inherit">
                            <Avatar sx={{ bgcolor: '#fff', color: '#d32f2f' }}>
                                {(user?.first_name || user?.username || '?')[0].toUpperCase()}
                            </Avatar>
                        </IconButton>

                        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                            <MenuItem disabled>{userRole}</MenuItem>
                            <Divider />
                            <MenuItem onClick={() => { navigate('/profile'); setAnchorEl(null); }}>
                                <AccountCircleIcon sx={{ mr: 1 }} /> Profile
                            </MenuItem>
                            <MenuItem onClick={() => { navigate('/legal/terms'); setAnchorEl(null); }}>
                                <SecurityIcon sx={{ mr: 1 }} /> Privacy & Legal
                            </MenuItem>
                            <MenuItem onClick={logout}>
                                <LogoutIcon sx={{ mr: 1 }} /> Logout
                            </MenuItem>
                        </Menu>
                    </Box>
                </Toolbar>
            </AppBar>

            {/* SIDEBAR */}
            <Box sx={{
                width: { sm: drawerWidth },
                flexShrink: 0,
                transition: TRANSITION,
            }}>
                {/* Mobile drawer */}
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    sx={{
                        display: { sm: 'none' },
                        '& .MuiDrawer-paper': { width: DRAWER_WIDTH },
                    }}
                >
                    {drawerContent(true)}
                </Drawer>

                {/* Desktop permanent drawer */}
                <Drawer
                    variant="permanent"
                    open
                    sx={{
                        display: { xs: 'none', sm: 'block' },
                        '& .MuiDrawer-paper': {
                            width: drawerWidth,
                            boxSizing: 'border-box',
                            overflowX: 'hidden',
                            transition: TRANSITION,
                        },
                    }}
                >
                    {drawerContent(false)}
                </Drawer>
            </Box>

            {/* MAIN CONTENT */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    mt: 8,
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    transition: TRANSITION,
                    minWidth: 0,
                }}
            >
                {/* Support both explicit children (legacy) and React Router <Outlet /> (layout route).
                    Own Suspense boundary here — not just the one around <Routes> in App.jsx —
                    so a lazy child route's chunk loading only shows a spinner in this content
                    area. Without it, Suspense propagates to the nearest ancestor boundary and
                    unmounts this ENTIRE AppLayout (sidebar included) on every first-visit
                    navigation to a lazily-loaded page, replacing it with App.jsx's full-page
                    Loader — a jarring flash that looks like the click did nothing, especially
                    on a slower connection, prompting repeated clicks on a sidebar item that
                    (falsely) appears unresponsive. */}
                <Suspense fallback={
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                }>
                    {children ?? <Outlet />}
                </Suspense>
            </Box>

            {/* Offline / stale-cache banner — fixed below the AppBar */}
            <OfflineBanner />

        </Box>
    );
};

export default AppLayout;
